import { Box, Button, IconButton, InputBase, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import { FONT_FAMILY_CSS, FS } from "@plannel/utils/font-util";
import useResizableWidth, { ResizeHandle } from "@plannel/components/ui/useResizableWidth";

/*
 * 우측 사이드 패널 셸 — Claude Design "PlanNEL Side Panels" 의 1b(Elevated) 안 구현.
 *
 * 소비처: DP Workbench 의 User Customization / Note / Advanced Filter 세 패널.
 * 지금까지는 셋이 각자 셸을 갖고 있었다(Note 는 350px 고정 + 청록 풀블리드 바,
 * Advanced Filter 는 45px 풀블리드 바, User Customization 은 MUI Dialog + SCSS `.title`).
 * 그래서 나란히 열면 헤더 높이·여백·버튼 위치가 전부 어긋났다. 여기 하나로 모은다.
 *
 * 셸의 뼈대는 헤더(아이콘 크레스트 + 제목 + 상태 부제 + 액션) / 본문(스크롤) / 푸터(선택) 세 칸이고,
 * 각 패널은 본문과 푸터만 채운다. 헤더 규격을 패널이 직접 그리지 않게 하는 것이 이 파일의 목적이다.
 *
 *   <SidePanel
 *     icon={<FilterAltIcon />}
 *     title={t("advancedFilter")}
 *     subtitle={`${count} ${t("condition")}`}
 *     onClose={handleClose}
 *     footer={<>…</>}
 *   >
 *     …본문…
 *   </SidePanel>
 *
 * ※ 시안은 Roboto + Material Symbols Rounded 를 쓰지만 여기서는 앱 폰트(`var(--app-font-family)`)와
 *   `@mui/icons-material` 를 그대로 쓴다. 폰트 스택을 새로 하드코딩하면 CJK 자형이 깨지고
 *   (saas-web/CLAUDE.md 의 "CJK 자형 / 폰트 스택" 참고), 아이콘 웹폰트를 하나 더 얹을 이유도 없다.
 *   글자 크기도 시안의 절대값 대신 FS 토큰을 쓴다 — Big/Small 토글이 살아 있어야 한다.
 *
 * ※ 토큰은 **시안 px 에 가장 가까운 단계**로 고른다. Small 기준 실제 값은
 *   CAPTION 10 / BODY 11 / SUB 12 / TITLE 13 / H2 14 (App.scss `:root[data-font-scale]`).
 *   역할 이름만 보고 본문을 BODY(11px) 로 잡으면 시안(12.5~13px)보다 작아지고,
 *   무엇보다 같은 패널의 MUI 입력(테마 기본 MuiInputBase = H1, 15px)과 4px 이나 벌어져
 *   한 화면 안에서 두 벌로 보인다. 매핑은 이렇게 둔다:
 *     패널 제목 H2 · 부제 SUB · 목록 행 제목/입력 TITLE · 행 메타·배지·대문자 라벨 CAPTION
 */

/*
 * 시안이 정한 색. 앱 팔레트(primary.main #048BA8)보다 한 단계 밝은 청록 계열이다
 * (시안의 hover 색 #0d8bab 이 앱 primary 와 거의 같고, 기본색이 그보다 밝다).
 * 팔레트를 앱 기준으로 되돌리려면 이 객체만 고치면 된다 — 소비처는 전부 여기를 참조한다.
 */
export const PANEL = {
  accent: "#12a3c6",        // 주 액션·활성 표시
  accentHover: "#0d8bab",
  accentDeep: "#0b5c8f",    // 아이콘 hover·아바타
  accentSoft: "#dff1f7",    // 크레스트 배경
  accentWash: "#f1f9fc",    // 활성 행 배경
  accentLine: "#cfe9f2",    // 활성 행 테두리
  accentLineHover: "#a9dcec",
  focusRing: "#b6e2ef",
  headGradient: "linear-gradient(180deg,#f2fafd,#ffffff)",
  headLine: "#eaf2f6",
  ink: "#12303f",           // 제목·값
  inkBody: "#41535f",       // 본문
  inkMuted: "#8a99a5",      // 부제
  inkLabel: "#93a1ab",      // 대문자 라벨
  inkFaint: "#a7b3bc",      // placeholder
  iconIdle: "#9aa8b2",
  iconFaint: "#c3ced6",
  sun: "#e08b84",           // 캘린더 요일 헤더 — 일요일
  sat: "#8fa8c0",           // 캘린더 요일 헤더 — 토요일
  line: "#e8edf1",          // 기본 행 테두리
  lineSoft: "#e2e9ee",      // 카드 내부 테두리
  fieldBg: "#f2f5f8",       // 알약 필드(검색)
  wellBg: "#f7fafb",        // 조건 카드 안쪽 필드·스레드 배경
  footBg: "#fbfdfe",
  footLine: "#eef2f5",
  danger: "#e03c31",        // 조건 삭제 × 의 hover
  dangerWash: "#fdeceb",    // 위험 동작이 켜져 있음을 알리는 알약 배경(accentSoft 의 위험색 짝)
  star: "#f0a93c",          // 기본 레이아웃 별
  shadow: "0 10px 32px rgba(11,60,92,.16), 0 1px 3px rgba(11,60,92,.1)",
  radius: "14px",
};

/* 헤더 우측 아이콘(설정·닫기) 공통 — 시안의 19px / #9aa8b2 / hover #0b5c8f */
export const PANEL_HEAD_ICON_SX = {
  p: 0.5,
  color: PANEL.iconIdle,
  "& > svg": { fontSize: 19 },
  "&:hover": { color: PANEL.accentDeep, bgcolor: "transparent" },
};

/* 대문자 소라벨 — SEARCH LEVEL / CUSTOMER / ITEM / TIME */
export const PANEL_LABEL_SX = {
  fontSize: FS.CAPTION,
  fontWeight: 500,
  letterSpacing: "0.7px",
  color: PANEL.inkLabel,
};

/*
 * 패널 헤더 — 아이콘 크레스트 + 제목 + 상태 부제 + 액션.
 *
 * SidePanel 안에서만 쓰이는 게 아니라 따로 export 한다: AdvancedFilter 는 호출부가 64곳이라
 * 카드 셸(height:100% + 자체 스크롤)로 바꾸면 그 화면들의 높이 계산을 전부 다시 맞춰야 한다.
 * 그래서 그쪽은 셸 없이 이 헤더만 얹어 같은 얼굴을 갖게 한다.
 */
export const PanelHeader = ({ icon, title, subtitle, actions, onClose, closeLabel, sx }) => (
  <Box
    sx={{
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: "11px",
      p: "15px 17px",
      background: PANEL.headGradient,
      borderBottom: `1px solid ${PANEL.headLine}`,
      /*
       * 앱에 전역 CssBaseline 이 없어 body 에 테마 폰트가 깔리지 않는다.
       * MUI Typography 계열은 테마에서 받지만 이 패널은 Box(div) 로 짜여 있어
       * 여기서 직접 주지 않으면 브라우저 기본 글꼴로 떨어진다.
       */
      fontFamily: FONT_FAMILY_CSS,
      ...sx,
    }}
  >
    {icon ? (
      <Box
        sx={{
          flex: "none",
          width: 32,
          height: 32,
          borderRadius: "10px",
          bgcolor: PANEL.accentSoft,
          color: PANEL.accentHover,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& > svg": { fontSize: 19 },
        }}
      >
        {icon}
      </Box>
    ) : null}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {/*
       * Typography 를 쓰지 않는다 — 전역 SCSS(.content-main .MuiTypography-root)가 font-size 를
       * 덮어써서 화면에 따라 제목만 커지는 사고가 난다(StatPill 과 같은 이유).
       */}
      <Box sx={{ fontSize: FS.H2, fontWeight: 500, lineHeight: 1.2, color: PANEL.ink }}>
        {title}
      </Box>
      {subtitle ? (
        <Box
          sx={{
            mt: "2px",
            fontSize: FS.SUB,
            color: PANEL.inkMuted,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {subtitle}
        </Box>
      ) : null}
    </Box>
    {actions}
    {onClose ? (
      <Tooltip title={closeLabel ?? ""}>
        <IconButton size="small" onClick={onClose} sx={PANEL_HEAD_ICON_SX}>
          <CloseIcon />
        </IconButton>
      </Tooltip>
    ) : null}
  </Box>
);

const SidePanel = ({
  id,
  icon,
  title,
  subtitle,
  actions,
  onClose,
  closeLabel,
  toolbar,
  footer,
  width = 372,
  resizable = false,
  minWidth = 320,
  children,
  sx,
  bodySx,
}) => {
  /*
   * `resizable` 이면 폭은 이 훅이 들고 있다 — 고급 필터 패널과 같은 손잡이·같은 규칙이다.
   * 나란히 선 세 패널 중 필터만 폭이 바뀌면, 같은 줄에서 하나만 다르게 움직이는 셈이 된다.
   * 손잡이는 패널 **밖**에 형제로 서야 한다(패널이 overflow: hidden 이라 안에 두면 잘린다)
   * → 그래서 호출부의 flex 행에 손잡이와 카드가 나란히 들어간다.
   */
  const resize = useResizableWidth({ defaultWidth: typeof width === "number" ? width : 372, minWidth });

  const panel = (
    <Box
      id={id}
      ref={resizable ? resize.nodeRef : undefined}
      sx={{
        boxSizing: "border-box",
        flex: "none",
        width: resizable ? `${resize.width}px` : width,
        maxWidth: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        bgcolor: "#fff",
        borderRadius: PANEL.radius,
        boxShadow: PANEL.shadow,
        overflow: "hidden",
        fontFamily: FONT_FAMILY_CSS,   // 위 PanelHeader 주석 참고 — 전역 body 폰트 규칙이 없다
        ...sx,
      }}
    >
      <PanelHeader
        icon={icon}
        title={title}
        subtitle={subtitle}
        actions={actions}
        onClose={onClose}
        closeLabel={closeLabel}
      />

      {/*
       * 본문 위에 고정되는 줄 — 검색처럼 "목록이 길어져도 늘 닿아야 하는" 컨트롤 자리.
       * 본문(children)만 스크롤하고 이 줄은 남는다. 목록이 길면 검색창까지 같이 밀려 올라가
       * 정작 찾을 방법이 사라지던 문제를 여기서 막는다.
       */}
      {toolbar ? (
        <Box sx={{ flex: "none", display: "flex", alignItems: "center", gap: "8px", p: "15px 17px 0" }}>
          {toolbar}
        </Box>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: toolbar ? "12px 17px 17px" : "15px 17px 17px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          /*
           * 본문이 세로 flex 라 자식은 기본값(flex-shrink: 1)대로 **줄어든다**.
           * 그래서 목록이 길어지면 넘쳐서 스크롤되는 게 아니라 카드가 통째로 찌그러지고
           * (실측: 카드 50px → 7px, scrollHeight 가 clientHeight 와 같아짐) 스크롤바가 아예 안 생겨
           * 아래쪽 내용을 볼 방법이 없어진다.
           * 세로로 스크롤하는 flex 컬럼에서는 자식이 자기 높이를 지켜야 한다.
           */
          "& > *": { flexShrink: 0 },
          ...bodySx,
        }}
      >
        {children}
      </Box>

      {footer ? (
        <Box
          sx={{
            flex: "none",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            p: "13px 17px",
            borderTop: `1px solid ${PANEL.footLine}`,
            bgcolor: PANEL.footBg,
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  );

  if (!resizable) {
    return panel;
  }

  return (
    <>
      <ResizeHandle onMouseDown={resize.startResize} active={resize.resizing} />
      {panel}
    </>
  );
};

/*
 * 검색 알약 — 높이 38 / radius 19 / 회색 필드, 포커스 시 링만 켜진다.
 * MUI TextField 를 쓰지 않는 이유: outlined 노치·라벨이 알약 모양과 싸운다.
 */
export const PanelSearchField = ({ value, onChange, onKeyPress, placeholder, endAdornment, sx }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "9px",
      height: 38,
      px: "14px",
      borderRadius: "19px",
      bgcolor: PANEL.fieldBg,
      transition: "box-shadow .15s ease",
      "&:focus-within": { boxShadow: `0 0 0 2px ${PANEL.focusRing}` },
      ...sx,
    }}
  >
    <SearchIcon sx={{ fontSize: 18, color: PANEL.iconIdle, flex: "none" }} />
    <InputBase
      value={value}
      onChange={onChange}
      onKeyPress={onKeyPress}
      placeholder={placeholder}
      sx={{
        flex: 1,
        fontSize: FS.TITLE,
        color: PANEL.ink,
        "& input::placeholder": { color: PANEL.inkFaint, opacity: 1 },
      }}
    />
    {endAdornment}
  </Box>
);

/*
 * 목록 행 — hover 시 1px 떠오르고 테두리가 청록으로 바뀐다.
 * 테두리는 border 가 아니라 inset box-shadow 다: 시안처럼 hover 에서 바깥 그림자와 겹칠 때
 * border 를 쓰면 1px 만큼 레이아웃이 흔들린다.
 */
export const PanelRow = ({ leading, title, meta, trailing, active, onClick, sx }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "11px",
      p: "12px 13px",
      borderRadius: "12px",
      cursor: onClick ? "pointer" : "default",
      bgcolor: active ? PANEL.accentWash : "transparent",
      boxShadow: `inset 0 0 0 1px ${active ? PANEL.accentLine : PANEL.line}`,
      transition: "transform .15s ease, box-shadow .15s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: active
          ? `inset 0 0 0 1px ${PANEL.accentLineHover}, 0 4px 12px rgba(18,163,198,.16)`
          : `inset 0 0 0 1px ${PANEL.accentLine}, 0 4px 12px rgba(11,60,92,.08)`,
      },
      ...sx,
    }}
  >
    {leading}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          fontSize: FS.TITLE,
          fontWeight: 500,
          color: PANEL.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </Box>
      {meta ? (
        <Box
          sx={{
            mt: "2px",
            fontSize: FS.CAPTION,
            color: active ? "#7d97a4" : PANEL.inkMuted,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {meta}
        </Box>
      ) : null}
    </Box>
    {trailing}
  </Box>
);

/* 활성 배지 — 시안의 ACTIVE 알약 */
export const PanelBadge = ({ children, sx }) => (
  <Box
    sx={{
      flex: "none",
      fontSize: FS.CAPTION,
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: "0.6px",
      color: "#fff",
      bgcolor: PANEL.accent,
      borderRadius: "9px",
      px: "9px",
      py: "2px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

/*
 * 아래 세 버튼은 **앱 표준 MUI Button** 을 그대로 쓴다.
 *
 * 시안은 청록 알약(radius 18 + 컬러 그림자)과 점선 CTA 로 그렸지만, 버튼은 화면을 넘나드는
 * 공통 어휘라 패널 안에서만 다른 모양이면 앱이 두 벌로 보인다.
 * 기준은 BaseConfigModal 의 푸터다 — 주 액션 `size="small" variant="contained" disableElevation`,
 * 보조·파괴적 액션은 텍스트 버튼. (아이콘 규칙: 저장=SaveIcon, 적용/확인=DoneAll — 호출부에서 넘긴다.)
 *
 * 이름과 prop 은 그대로 둔다 — 소비처가 어떤 자리의 버튼인지 읽히게 하고,
 * 나중에 룩을 다시 바꿔도 여기 한 곳만 고치면 되게.
 */

/* 보조 액션 — "+ 조건" / "+ 그룹"(pill) 과 패널 하단의 블록 액션 */
export const PanelDashedButton = ({ icon, children, onClick, muted, pill, disabled, sx }) => (
  <Button
    size="small"
    variant={pill ? "text" : "outlined"}
    color={muted ? "inherit" : "primary"}
    startIcon={icon}
    onClick={onClick}
    disabled={disabled}
    sx={pill ? sx : { width: "100%", ...sx }}
  >
    {children}
  </Button>
);

/* 주 액션 — 적용(Confirm) / 저장(Save) */
export const PanelPrimaryButton = ({ icon, children, onClick, disabled, sx }) => (
  <Button
    size="small"
    variant="contained"
    disableElevation
    startIcon={icon}
    onClick={onClick}
    disabled={disabled}
    sx={{ minWidth: 72, ...sx }}
  >
    {children}
  </Button>
);

/* 파괴적 액션 — 전체 삭제 */
export const PanelDangerButton = ({ icon, children, onClick, sx }) => (
  <Button size="small" color="error" startIcon={icon} onClick={onClick} sx={sx}>
    {children}
  </Button>
);

export default SidePanel;
